// DATA
import * as advancedCalculators from "../engineering/advanced-calculators";

// INTERFACES
import {
  AdvancedCalculator,
  AdvancedCalculatorInput,
  AppData,
  BaseQuantity,
} from "../interface/functionalityInterface";
import {
  AdvancedCalculatorUnitProps,
  CalculatorUnitProps,
} from "../interface/propTypesInterface";

// NPM INSTALLS
import { v4 as UUID } from "uuid";

const definitions = Object.values(
  advancedCalculators
) as AdvancedCalculator[];

const findDefinition = (definitionIdOrName: string) =>
  definitions.find(
    (definition) =>
      definition.definitionId === definitionIdOrName ||
      definition.name === definitionIdOrName
  );

export const isAdvancedCalculatorName = (calcName: string) =>
  definitions.some((definition) => definition.name === calcName);

const cloneInputs = (
  inputs: AdvancedCalculatorInput[],
  storedInputs: BaseQuantity[] = []
) =>
  inputs.map((input) => {
    const storedInput = storedInputs.find(
      (candidate) => candidate.name === input.name
    );

    return {
      ...input,
      ...storedInput,
      id: storedInput?.id ?? UUID(),
      type: input.type,
      baseUnit: input.baseUnit,
      units: input.units,
      clearValue: input.clearValue,
      section: input.section,
    };
  });

const legacyResultUnits = (
  resultUnits: Record<string, string> = {}
): Record<string, string | undefined> => ({
  emptying_time: resultUnits.emptying_time ?? resultUnits.emptyingTime,
  initial_flow: resultUnits.initial_flow ?? resultUnits.initialFlow,
  tank_area: resultUnits.tank_area ?? resultUnits.tankArea,
  outlet_area: resultUnits.outlet_area ?? resultUnits.outletArea,
});

export const createAdvancedCalculator = (
  calcName: string
): AdvancedCalculatorUnitProps => {
  const definition = findDefinition(calcName);
  if (!definition) {
    throw new Error(`no advanced calculator name: ${calcName}`);
  }

  return {
    ...definition,
    id: UUID(),
    noDecimals: 1,
    inputs: cloneInputs(definition.inputs),
    outputs: definition.outputs.map((output) => ({
      ...output,
      id: UUID(),
    })),
    resultUnits: Object.fromEntries(
      definition.outputs.map((output) => [
        output.name,
        output.selectedUnit,
      ])
    ),
    removeUnit: () => {},
    duplicateUnit: () => {},
    addToReceipt: () => {},
    dragDrop: () => {},
  };
};

export const duplicateAdvancedCalculator = (
  calc: AdvancedCalculatorUnitProps
): AdvancedCalculatorUnitProps => ({
  ...calc,
  id: UUID(),
  graphConfiguration: calc.graphConfiguration
    ? {
        ...calc.graphConfiguration,
        legends: calc.graphConfiguration.legends.map((legend) => ({
          ...legend,
          id: UUID(),
        })),
        savedPoints: (calc.graphConfiguration.savedPoints ?? []).map(
          (point) => ({
            ...point,
            id: UUID(),
            values: point.values.map((value) => ({ ...value })),
          })
        ),
      }
    : undefined,
  inputs: calc.inputs.map((input) => ({
    ...input,
    id: UUID(),
  })),
  outputs: calc.outputs.map((output) => ({
    ...output,
    id: UUID(),
  })),
});

export const hydrateAdvancedCalculator = (
  storedCalculator:
    | AdvancedCalculatorUnitProps
    | CalculatorUnitProps
    | Record<string, any>
): AdvancedCalculatorUnitProps | null => {
  const storedData = storedCalculator as Record<string, any>;
  const isLegacyTankCalculator =
    storedData.type === AppData.CALCULATOR &&
    storedData.renderer === "tank-emptying";
  const definition = findDefinition(
    isLegacyTankCalculator
      ? "tank-emptying"
      : storedData.definitionId ?? storedData.name
  );

  if (!definition) return null;

  const storedInputs = isLegacyTankCalculator
    ? storedData.inputs?.[storedData.selectedCalcMode ?? 0]
        ?.baseQuantities ?? []
    : storedData.inputs ?? [];
  const savedResultUnits = legacyResultUnits(
    storedData.resultUnits
  );

  return {
    ...definition,
    ...storedData,
    type: AppData.ADVANCED_CALCULATOR,
    definitionId: definition.definitionId,
    id: storedData.id ?? UUID(),
    noDecimals: storedData.noDecimals ?? 1,
    inputs: cloneInputs(definition.inputs, storedInputs),
    outputs: definition.outputs.map((output) => {
      const storedOutput = storedData.outputs?.find(
        (candidate: BaseQuantity) => candidate.name === output.name
      );
      return {
        ...output,
        ...storedOutput,
        id: storedOutput?.id ?? UUID(),
        baseUnit: output.baseUnit,
        units: output.units,
        decimalsOffset: output.decimalsOffset,
        section: output.section,
      };
    }),
    resultUnits: Object.fromEntries(
      definition.outputs.map((output) => [
        output.name,
        savedResultUnits[output.name] ?? output.selectedUnit,
      ])
    ),
    calculate: definition.calculate,
    validate: definition.validate,
    chart: definition.chart,
    removeUnit: () => {},
    duplicateUnit: () => {},
    addToReceipt: () => {},
    dragDrop: () => {},
  };
};
